// Consent-gated analytics for everydayworkstoday.com.
// Nothing here loads Google Analytics or PostHog until the visitor explicitly
// opts in via the on-site consent banner ("Accept Analytics"). Advertising
// storage is never granted (this site runs no advertising trackers).

export const GA_MEASUREMENT_ID = 'G-FVF7V535MG';
const POSTHOG_KEY = 'phc_xAvL2Iq4tFmANRE7kzbKwaSqp1HJjN7x48s3vr0CMjs';
const POSTHOG_HOST = 'https://us.i.posthog.com';

export const CONSENT_VERSION = 1;
const CONSENT_KEY = 'ew_cookie_consent';

// ---- Stored preference (version + timestamp) ----------------------------------
export function getConsent() {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.version !== CONSENT_VERSION) return null; // re-ask on version bump
    if (parsed.status !== 'accepted' && parsed.status !== 'rejected') return null;
    return parsed; // { status, version, timestamp }
  } catch {
    return null;
  }
}

export function saveConsent(status) {
  const record = { status, version: CONSENT_VERSION, timestamp: new Date().toISOString() };
  try {
    localStorage.setItem(CONSENT_KEY, JSON.stringify(record));
  } catch {
    /* storage unavailable — analytics simply stays off */
  }
  return record;
}

function ensureGtag() {
  if (typeof window === 'undefined') return null;
  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag !== 'function') {
    window.gtag = function () { window.dataLayer.push(arguments); };
  }
  return window.gtag;
}

// ---- Loaders (idempotent) -----------------------------------------------------
function loadGA() {
  if (window.__ewGaLoaded) return;
  window.__ewGaLoaded = true;
  const gtag = ensureGtag();
  window['ga-disable-' + GA_MEASUREMENT_ID] = false;
  const s = document.createElement('script');
  s.async = true;
  s.src = 'https://www.googletagmanager.com/gtag/js?id=' + GA_MEASUREMENT_ID;
  document.head.appendChild(s);
  gtag('js', new Date());
  // send_page_view:false — SPA page views are sent manually (see pageview()).
  gtag('config', GA_MEASUREMENT_ID, { send_page_view: false, anonymize_ip: true });
}

function loadPostHog() {
  if (window.__ewPhLoaded) return;
  window.__ewPhLoaded = true;
  // Official PostHog stub loader — array.js is only fetched when init() is called.
  !(function (t, e) {
    var o, n, p, r;
    e.__SV ||
      ((window.posthog = e),
      (e._i = []),
      (e.init = function (i, s, a) {
        function g(t, e) {
          var o = e.split('.');
          2 == o.length && ((t = t[o[0]]), (e = o[1])),
            (t[e] = function () { t.push([e].concat(Array.prototype.slice.call(arguments, 0))); });
        }
        ((p = t.createElement('script')).type = 'text/javascript'),
          (p.crossOrigin = 'anonymous'),
          (p.async = !0),
          (p.src = s.api_host.replace('.i.posthog.com', '-assets.i.posthog.com') + '/static/array.js'),
          (r = t.getElementsByTagName('script')[0]).parentNode.insertBefore(p, r);
        var u = e;
        for (
          void 0 !== a ? (u = e[a] = []) : (a = 'posthog'),
            u.people = u.people || [],
            u.toString = function (t) { var e = 'posthog'; return 'posthog' !== a && (e += '.' + a), t || (e += ' (stub)'), e; },
            u.people.toString = function () { return u.toString(1) + '.people (stub)'; },
            o = 'init capture register register_once unregister identify setPersonProperties group reset get_distinct_id get_session_id opt_in_capturing opt_out_capturing has_opted_out_capturing has_opted_in_capturing clear_opt_in_out_capturing'.split(' '),
            n = 0;
          n < o.length;
          n++
        )
          g(u, o[n]);
        e._i.push([i, s, a]);
      }),
      (e.__SV = 1));
  })(document, window.posthog || []);
  window.posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    person_profiles: 'identified_only',
    capture_pageview: false, // SPA page views are sent manually
    disable_session_recording: true,
    disable_surveys: true,
  });
}

// ---- Grant / revoke -----------------------------------------------------------
export function grantAnalytics() {
  const gtag = ensureGtag();
  // Only analytics_storage is granted; advertising signals stay denied.
  gtag('consent', 'update', { analytics_storage: 'granted' });
  loadGA();
  loadPostHog();
}

function clearAnalyticsCookies() {
  if (typeof document === 'undefined') return;
  const names = document.cookie
    .split(';')
    .map((c) => c.split('=')[0].trim())
    .filter(Boolean);
  const targets = names.filter(
    (n) => /^_ga($|_)/.test(n) || n === '_gid' || /^_gat/.test(n) || /^ph_/.test(n) || /^__ph/.test(n)
  );
  const host = window.location.hostname;
  const labels = host.split('.');
  const domains = [host, '.' + host];
  // Every parent-domain suffix, so we match whatever domain the vendor used
  // (e.g. ".preview.emergentagent.com" or ".everydayworkstoday.com").
  for (let i = 1; i < labels.length - 1; i++) {
    domains.push('.' + labels.slice(i).join('.'));
  }
  const expire = 'expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/';
  targets.forEach((name) => {
    document.cookie = `${name}=; ${expire}`;
    domains.forEach((d) => {
      document.cookie = `${name}=; ${expire}; domain=${d}`;
    });
  });
}

export function disableAnalytics() {
  const gtag = ensureGtag();
  gtag('consent', 'update', { analytics_storage: 'denied' });
  window['ga-disable-' + GA_MEASUREMENT_ID] = true;
  // Note: we intentionally do NOT call posthog.opt_out_capturing()/reset() here —
  // those re-persist a PostHog cookie asynchronously. The withdrawal flow reloads
  // the page (see ConsentContext), after which PostHog never re-initializes, so
  // clearing the cookies below is sufficient and permanent.
  clearAnalyticsCookies();
}

// ---- Tracking (no-ops unless consent === accepted) ----------------------------
export const pageview = (url) => {
  if (getConsent()?.status !== 'accepted') return;
  grantAnalytics(); // idempotent — guarantees config precedes the event
  if (typeof window.gtag === 'function') {
    window.gtag('event', 'page_view', {
      page_path: url,
      page_location: window.location.href,
      page_title: document.title,
    });
  }
  try { window.posthog && window.posthog.capture && window.posthog.capture('$pageview'); } catch {}
};

export const trackEvent = (eventName, params = {}) => {
  if (getConsent()?.status !== 'accepted') return;
  if (typeof window.gtag === 'function') {
    window.gtag('event', eventName, params);
  }
};

export const analytics = {
  trackSubscribe: (method = 'footer') => trackEvent('sign_up', { method, event_category: 'engagement' }),
  trackContact: (subject) => trackEvent('generate_lead', { event_category: 'engagement', event_label: subject }),
  trackAppDownload: (appName, platform) => trackEvent('select_content', { content_type: 'app_download', item_id: appName, platform }),
  trackBlogView: (postTitle, postCategory) => trackEvent('view_item', { content_type: 'blog_post', item_id: postTitle, item_category: postCategory }),
  trackOutboundLink: (url) => trackEvent('click', { event_category: 'outbound', event_label: url, transport_type: 'beacon' }),
};
